import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Minus, Trash2, Columns, Rows, Table } from 'lucide-react';
import { Editor } from '@tiptap/react';
import { tableOperationHelpers } from '../../../utils/editor/editorHelpers';

import { logger } from '@/utils/logger';

interface TableContextMenuProps {
  editor: Editor | null;
}

type TableAction = 
  | 'addRowBefore' 
  | 'addRowAfter' 
  | 'addColBefore' 
  | 'addColAfter' 
  | 'deleteRow' 
  | 'deleteCol' 
  | 'deleteTable';

interface Position {
  top: number;
  left: number;
}

// Table action handlers moved to tableActions.ts

const TableContextMenu: React.FC<TableContextMenuProps> = React.memo(({ editor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  const handleContextMenu = useCallback((e: MouseEvent) => {
    const target = e.target as Element;
    const tableElement = target.closest('td, th, table');
    if (!tableElement || !editor?.isEditable) return;
    
    e.preventDefault();
    
    // Smart positioning to keep menu on screen
    const menuWidth = 224; // w-56 = 14rem = 224px
    const menuHeight = 280; // Approximate height
    const padding = 8;
    
    let left = e.clientX;
    let top = e.clientY;
    
    // Check if menu would go off right edge
    if (left + menuWidth + padding > window.innerWidth) {
      left = window.innerWidth - menuWidth - padding;
    }
    
    // Check if menu would go off bottom edge
    if (top + menuHeight + padding > window.innerHeight) {
      top = window.innerHeight - menuHeight - padding;
    }
    
    // Ensure menu doesn't go off left or top edges
    left = Math.max(padding, left);
    top = Math.max(padding, top);
    
    setPosition({ top, left });
    setIsOpen(true);
  }, [editor]);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleContextMenu, handleClickOutside]);

  const handleAction = useCallback((action: TableAction) => {
    if (!editor) return;

    try {
      switch (action) {
        case 'addRowBefore': 
          tableOperationHelpers.insertRowBefore(editor);
          break;
        case 'addRowAfter': 
          tableOperationHelpers.insertRowAfter(editor);
          break;
        case 'addColBefore': 
          tableOperationHelpers.insertColumnBefore(editor);
          break;
        case 'addColAfter': 
          tableOperationHelpers.insertColumnAfter(editor);
          break;
        case 'deleteRow': 
          tableOperationHelpers.deleteRow(editor);
          break;
        case 'deleteCol': 
          tableOperationHelpers.deleteColumn(editor);
          break;
        case 'deleteTable': 
          tableOperationHelpers.deleteTable(editor);
          break;
        default: 
          break;
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        logger.error('Table action failed:', error instanceof Error ? error.message : 'Unknown error');
      }
    } finally {
      setIsOpen(false);
    }
  }, [editor]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[9999] theme-surface shadow-xl rounded-md py-2 w-56 border theme-border"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`
      }}
    >
      <div className="px-3 py-2 text-sm font-medium theme-text border-b flex items-center">
        <Table size={16} className="mr-2" />
        Table Options
      </div>
      <button 
        className="w-full text-left px-3 py-2 text-sm flex items-center hover:theme-muted theme-text"
        onClick={() => handleAction('addRowBefore')}
      >
        <Rows size={16} className="mr-2" />
        Insert Row Above
      </button>
      <button 
        className="w-full text-left px-3 py-2 text-sm flex items-center hover:theme-muted theme-text"
        onClick={() => handleAction('addRowAfter')}
      >
        <Rows size={16} className="mr-2" />
        Insert Row Below
      </button>
      <button 
        className="w-full text-left px-3 py-2 text-sm flex items-center hover:theme-muted theme-text"
        onClick={() => handleAction('addColBefore')}
      >
        <Columns size={16} className="mr-2" />
        Insert Column Left
      </button>
      <button 
        className="w-full text-left px-3 py-2 text-sm flex items-center hover:theme-muted theme-text"
        onClick={() => handleAction('addColAfter')}
      >
        <Columns size={16} className="mr-2" />
        Insert Column Right
      </button>
      <div className="border-t my-1"></div>

      <button 
        className="w-full text-left px-3 py-2 text-sm flex items-center hover:theme-muted text-red-600"
        onClick={() => handleAction('deleteRow')}
      >
        <Minus size={16} className="mr-2" />
        Delete Row
      </button>
      <button 
        className="w-full text-left px-3 py-2 text-sm flex items-center hover:theme-muted text-red-600"
        onClick={() => handleAction('deleteCol')}
      >
        <Minus size={16} className="mr-2" />
        Delete Column
      </button>
      <button 
        className="w-full text-left px-3 py-2 text-sm flex items-center hover:theme-muted text-red-600"
        onClick={() => handleAction('deleteTable')}
      >
        <Trash2 size={16} className="mr-2" />
        Delete Table
      </button>
    </div>,
    document.body
  );
});

TableContextMenu.displayName = 'TableContextMenu';

export default TableContextMenu; 